<template>
    <div v-if="series && series.events">
        <EventList
            :loading="loading"
            :is-empty="isEmpty"
            :loading-message="$t('loading-events')"
            :empy-state-description="$t('no-events')"
            :create-disabled-explanation="createEventDisabledExplanation"
            :create-link="getCreateEventLink(series.id, series.nameLong)"
            :create-label="createEventButtonLabel"
        >
            <Message
                v-if="levelsWithoutElevation.length > 0"
                status="error"
                :headline="$t('level-missing-elevation-headline')"
            >
                <div class="py-2">{{ $t('level-missing-elevation-text') }}</div>
                <ul>
                    <li v-for="level in levelsWithoutElevation" :key="level.id" class="tabular-nums">
                        {{ level.nameShort }}
                    </li>
                </ul>
            </Message>
            <Message
                v-if="duplicateLevelsElevation.length > 0"
                status="error"
                :headline="$t('duplicate-levels-elevation-headline')"
            >
                <div class="py-2">{{ $t('duplicate-levels-elevation-text') }}</div>
                <ul>
                    <li v-for="group in duplicateLevelsElevation" :key="group.buildingId" class="tabular-nums">
                        {{ group.buildingName }}:
                        {{ group.levels.map((l) => l.nameShort).join(', ') }}
                    </li>
                </ul>
            </Message>
            <CollapsibleHeadline
                v-for="[grouping, periodEvents] in groupedEvents"
                :key="grouping"
                :heading-level="3"
                :headline-id="grouping"
                :initial-is-open="['mifri', 'future'].includes(grouping)"
            >
                <template #headline>
                    {{ $t('event-grouping-label', { grouping: grouping, count: periodEvents.length }) }}
                </template>
                <template #content>
                    <div v-if="periodEvents.length > 0 && eventPhaseTypes.length > 0" class="space-y-2">
                        <EventCard
                            v-for="event in periodEvents"
                            :key="event.id"
                            :link-target="getEventMainRoute(event.id)"
                            :link-text="event.nameShort"
                            :status-badge-variant="eventStatusToBadgeVariant(event.status)"
                            :status-badge-label="getEventStatusLabel(event.status)"
                            :variants-total-count="event.eventVariants.totalCount"
                            :event-id="event.id"
                            :execution-year="event.executionYear"
                            :variant="getEventMainVariant(event)"
                            :phase-types="eventPhaseTypes"
                            :link-to-reference-allocation="{
                                name: 'event-allocations-tab',
                                params: { eventId: event.id },
                            }"
                            :reference-allocation-query-document="getReferenceAllocationQueryDocument(event)"
                            :reference-allocation-link-label="$t('phases-tile-title-reference-allocation')"
                        />
                    </div>
                    <div v-else class="space-y-2 font-light text-gray-600">({{ $t('no-events-in-this-period') }})</div>
                </template>
            </CollapsibleHeadline>
        </EventList>
    </div>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import { BadgeVariant } from '@/components/Badge/Badge.vue';
import CollapsibleHeadline from '@/components/CollapsibleHeadline/CollapsibleHeadline.vue';
import Message from '@/components/Message/Message.vue';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import EventCard from '@/event/components/EventCard.vue';
import EventList from '@/event/components/EventList.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import {
    eventStatusToBadgeVariant,
    eventVariantStatusToBadgeVariant,
    getCreateEventLink,
    getEventMainRoute,
    getEventStatusLabel,
    getEventVariantRoute,
} from '@/event/event';
import {
    AllocationAreaPhaseTileDataDocument,
    AllocationRoomPhaseTileDataDocument,
    GetAllocationUsagesDocument,
    GetLevelsWithElevationDocument,
    GetLevelsWithoutElevationDocument,
    SeriesEventDataFragment,
} from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { DateTime } from 'luxon';
import { computed } from 'vue';

const fluent = useFluent();

defineProps<{ seriesId: string }>();

const { series, loading } = useEventSeriesInjection();

const { result: allocationUsages } = useQuery(GetAllocationUsagesDocument);
const eventPhaseTypes = computed(
    () =>
        allocationUsages.value?.allocationUsages?.nodes.map((n) => ({
            ...n,
            color: mapToPhaseColor(n.type),
        })) ?? [],
);

const events = computed(() => series.value?.events?.nodes ?? []);
const isEmpty = computed(() => (events.value ? events.value.length === 0 : true));

const now = DateTime.now();
const currentYear = now.year;
const inFiveYears = now.plus({ years: 5 }).year;

const groupingPeriods = ['past', 'mifri', 'future'] as const;

const groupedEvents = computed(() => {
    const groupedObjects = Object.groupBy(events.value, (event) => {
        if (event.executionYear < currentYear) {
            return 'past';
        }
        if (event.executionYear > inFiveYears) {
            return 'future';
        }
        return 'mifri';
    });
    const periodMap = new Map<string, SeriesEventDataFragment[]>();
    for (const period of groupingPeriods) {
        const sortedObjects =
            groupedObjects[period]?.toSorted(
                (a, b) => a.executionYear - b.executionYear || a.nameShort.localeCompare(b.nameShort),
            ) ?? [];
        periodMap.set(period, sortedObjects);
    }
    return periodMap;
});

const createEventDisabledExplanation = computed(() => {
    if (!auth.isAdmin()) {
        return fluent.$t('no-permission-to-create');
    }
    if (series.value && series.value.singleEvent && events.value && events.value.length >= 1) {
        return fluent.$t('single-event-limit-reached');
    }
    return undefined;
});

const createEventButtonLabel = computed(() =>
    series.value?.singleEvent ? fluent.$t('single-event-create') : fluent.$t('event-create'),
);

const { result: levelsWithoutElevationResult } = useQuery(GetLevelsWithoutElevationDocument, {
    levelTypeName: 'Hallenausstellungsebene',
});
const levelsWithoutElevation = computed(() => levelsWithoutElevationResult.value?.levels?.nodes ?? []);

const { result: levelsWithElevationResult } = useQuery(GetLevelsWithElevationDocument, {
    levelTypeName: 'Hallenausstellungsebene',
});

const duplicateLevelsElevation = computed(() => {
    const buildings = levelsWithElevationResult.value?.buildings?.nodes ?? [];

    const duplicateGroups = [];

    for (const building of buildings) {
        const levels = building.levels?.nodes ?? [];

        // Skip buildings with less than 2 levels
        if (levels.length < 2) {
            continue;
        }

        const elevationGroups = Object.groupBy(levels, (level) => level.elevation?.value);

        for (const [elevation, elevationLevels] of Object.entries(elevationGroups)) {
            if (elevationLevels && elevationLevels.length > 1) {
                duplicateGroups.push({
                    buildingId: building.id,
                    buildingName: building.nameShort,
                    elevation,
                    levels: elevationLevels,
                });
            }
        }
    }

    return duplicateGroups;
});

function getReferenceAllocationQueryDocument(event: SeriesEventDataFragment) {
    const mainVariant = event.mainVariant.nodes[0];
    if (mainVariant.referenceAllocationAreaId) {
        return AllocationAreaPhaseTileDataDocument;
    }
    return AllocationRoomPhaseTileDataDocument;
}

function getEventMainVariant(event: SeriesEventDataFragment) {
    if (event.mainVariant.nodes) {
        return event.mainVariant?.nodes.map((variant) => ({
            id: variant.id,
            index: variant.index,
            status: variant.status,
            statusBadgeVariant: eventVariantStatusToBadgeVariant(variant.status) as BadgeVariant,
            statusBadgeLabel: fluent.$t('event-variant-status', { status: variant.status }),
            linkTarget: getEventVariantRoute(event.id, variant.index),
            referenceAllocationId: variant.referenceAllocationRoomId ?? variant.referenceAllocationAreaId ?? undefined,
        }))[0];
    }
}
</script>
